import React, { useEffect, useRef } from 'react';

// This scene paints its own dark-navy gradient every frame (see
// drawBackground()) rather than reading the page theme -- a <canvas> can't
// resolve CSS custom properties the way DOM elements can, and re-theming a
// whole particle/gradient system for light mode is its own project. Simplest
// correct behavior for now: don't render it in light mode, so light-mode
// visitors get the plain parchment background instead of a dark scene
// showing through behind light-mode content.
const prefersLight = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-color-scheme: light)').matches;

const BackgroundCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (prefersLight) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0;

    function rand(min, max){ return min + Math.random() * (max - min); }
    function clamp(v, min, max){ return Math.max(min, Math.min(max, v)); }
    function lerp(a, b, t){ return a + (b - a) * t; }
    function pick(arr){ return arr[Math.floor(rand(0, arr.length))]; }

    function roundRect(ctx, x, y, w, h, r){
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    function drawTracked(ctx, text, cx, cy, fontSize, tracking, fontFamily){
      ctx.font = fontSize + 'px ' + (fontFamily || "'Segoe UI', sans-serif");
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      var chars = text.split('');
      var widths = chars.map(function(c){ return ctx.measureText(c).width; });
      var total = widths.reduce(function(a, b){ return a + b; }, 0) + tracking * (chars.length - 1);
      var x = cx - total / 2;
      for (var i = 0; i < chars.length; i++){
        ctx.fillText(chars[i], x, cy);
        x += widths[i] + tracking;
      }
    }

    var COLORS = {
      agentPalette: ['#6fb7ff', '#7fe3c4', '#a58bff'],
      gold: '#f2c572',
      goldBright: '#ffe1a1',
      amber: '#ffb347',
      danger: '#ff5d5d'
    };

    // ---------------------------------------------------------------------
    // Economic agents (network nodes)
    // ---------------------------------------------------------------------
    function Agent(w, h){
      this.r = rand(2.5, 5);
      this.x = rand(this.r, w - this.r);
      this.y = rand(this.r, h - this.r);
      var speed = rand(12, 30);
      var angle = rand(0, Math.PI * 2);
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed;
      this.color = pick(COLORS.agentPalette);
      this.pulse = rand(0, Math.PI * 2);
      this.lastTrade = -Infinity;
    }
    Agent.prototype.update = function(dt, w, h, speedMult){
      this.x += this.vx * dt * speedMult;
      this.y += this.vy * dt * speedMult;
      if (this.x < this.r){ this.x = this.r; this.vx *= -1; }
      if (this.x > w - this.r){ this.x = w - this.r; this.vx *= -1; }
      if (this.y < this.r){ this.y = this.r; this.vy *= -1; }
      if (this.y > h - this.r){ this.y = h - this.r; this.vy *= -1; }
      this.pulse += dt * 2;
    };
    Agent.prototype.draw = function(ctx){
      var glow = 0.5 + 0.5 * Math.sin(this.pulse);
      ctx.save();
      ctx.fillStyle = this.color;
      ctx.shadowColor = this.color;
      ctx.shadowBlur = 5 + glow * 4;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };
    Agent.prototype.scatter = function(){
      var speed = rand(60, 140);
      var angle = rand(0, Math.PI * 2);
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed;
    };

    // ---------------------------------------------------------------------
    // A brief glowing trade connection between two agents
    // ---------------------------------------------------------------------
    function TradeLink(a, b){
      this.a = a;
      this.b = b;
      this.age = 0;
      this.duration = 0.6;
    }
    Object.defineProperty(TradeLink.prototype, 'alive', {
      get: function(){ return this.age < this.duration; }
    });
    TradeLink.prototype.update = function(dt){ this.age += dt; };
    TradeLink.prototype.draw = function(ctx){
      var t = 1 - this.age / this.duration;
      ctx.save();
      ctx.globalAlpha = t * 0.9;
      ctx.strokeStyle = COLORS.amber;
      ctx.shadowColor = COLORS.goldBright;
      ctx.shadowBlur = 10 * t;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(this.a.x, this.a.y);
      ctx.lineTo(this.b.x, this.b.y);
      ctx.stroke();
      ctx.restore();
    };

    // ---------------------------------------------------------------------
    // Floating micro-text spawned on trade ("+Value", "Price Δ" ...)
    // ---------------------------------------------------------------------
    var PARTICLE_TEXTS = ['+Value', 'Price Δ', 'ΔP > 0', '+Info', 'Trade+'];
    function PriceParticle(x, y){
      this.x = x;
      this.y = y;
      this.text = pick(PARTICLE_TEXTS);
      this.age = 0;
      this.duration = 1.2;
      this.vy = -rand(14, 22);
      this.vx = rand(-6, 6);
    }
    Object.defineProperty(PriceParticle.prototype, 'alive', {
      get: function(){ return this.age < this.duration; }
    });
    PriceParticle.prototype.update = function(dt){
      this.age += dt;
      this.x += this.vx * dt;
      this.y += this.vy * dt;
    };
    PriceParticle.prototype.draw = function(ctx){
      var t = this.age / this.duration;
      ctx.save();
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = COLORS.goldBright;
      ctx.font = '11px "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(this.text, this.x, this.y);
      ctx.restore();
    };

    // ---------------------------------------------------------------------
    // Classic Austrian School quotes, drifting typography
    // ---------------------------------------------------------------------
    var QUOTES = [
      { text: 'PRICES ARE A MECHANISM FOR COMMUNICATING INFORMATION.', author: 'F. A. HAYEK' },
      { text: 'ECONOMICS IS NOT ABOUT THINGS; IT DEALS WITH THE ACTIONS OF MEN.', author: 'LUDWIG VON MISES' },
      { text: 'HUMAN ACTION IS PURPOSEFUL BEHAVIOR.', author: 'LUDWIG VON MISES' },
      { text: 'VALUE DOES NOT EXIST OUTSIDE THE CONSCIOUSNESS OF MEN.', author: 'CARL MENGER' },
      { text: 'THE CURIOUS TASK OF ECONOMICS IS TO SHOW MEN HOW LITTLE THEY KNOW ABOUT WHAT THEY IMAGINE THEY CAN DESIGN.', author: 'F. A. HAYEK' },
      { text: 'THE MARKET IS A DISCOVERY PROCEDURE.', author: 'F. A. HAYEK' },
      { text: 'MONEY IS AN OUTGROWTH OF THE MARKET, NOT A CREATION OF THE STATE.', author: 'CARL MENGER' }
    ];

    function QuoteInstance(w, h){
      this.quote = pick(QUOTES);
      this.x = rand(w * 0.2, w * 0.8);
      this.y = rand(h * 0.2, h * 0.75);
      this.w = w;
      this.age = 0;
      this.fadeIn = 1.6;
      this.hold = 4.5;
      this.fadeOut = 1.8;
      this.duration = this.fadeIn + this.hold + this.fadeOut;
    }
    Object.defineProperty(QuoteInstance.prototype, 'alive', {
      get: function(){ return this.age < this.duration; }
    });
    QuoteInstance.prototype.update = function(dt){
      this.age += dt;
      this.y -= 5 * dt;
    };
    QuoteInstance.prototype.alpha = function(){
      if (this.age < this.fadeIn) return this.age / this.fadeIn;
      if (this.age < this.fadeIn + this.hold) return 1;
      var t = (this.age - this.fadeIn - this.hold) / this.fadeOut;
      return 1 - t;
    };
    QuoteInstance.prototype.draw = function(ctx){
      var a = this.alpha() * 0.85;
      if (a <= 0) return;
      var text = this.quote.text;
      var fontSize = clamp((this.w * 0.78) / (text.length * 0.56), 11, 24);
      ctx.save();
      ctx.globalAlpha = a;
      ctx.fillStyle = COLORS.gold;
      ctx.shadowColor = COLORS.amber;
      ctx.shadowBlur = 14;
      drawTracked(ctx, text, this.x, this.y, fontSize, 3, "'Segoe UI', sans-serif");
      ctx.shadowBlur = 0;
      ctx.fillStyle = COLORS.amber;
      ctx.font = '11px "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('- ' + this.quote.author, this.x, this.y + fontSize + 8);
      ctx.restore();
    };

    // ---------------------------------------------------------------------
    // Spontaneous Market Index (bottom-right ticker)
    // ---------------------------------------------------------------------
    function MarketIndex(){
      this.value = 50;
      this.history = [];
      this.maxPoints = 60;
      this.tradeBuffer = 0;
      this.timer = 0;
    }
    MarketIndex.prototype.registerTrade = function(){ this.tradeBuffer++; };
    MarketIndex.prototype.update = function(dt, volMult){
      this.timer += dt;
      var interval = 0.25;
      while (this.timer >= interval){
        this.timer -= interval;
        var tradeEffect = this.tradeBuffer * 3;
        this.tradeBuffer = 0;
        var noise = rand(-1.4, 1.4);
        this.value += (tradeEffect + noise) * volMult - 1.1;
        this.value = clamp(this.value, 0, 100);
        this.history.push(this.value);
        if (this.history.length > this.maxPoints) this.history.shift();
      }
    };
    MarketIndex.prototype.draw = function(ctx, w, h){
      var panelW = 190, panelH = 92, margin = 16;
      var px = w - panelW - margin, py = h - panelH - margin;
      ctx.save();
      ctx.fillStyle = 'rgba(10,14,22,0.6)';
      ctx.strokeStyle = 'rgba(242,197,114,0.32)';
      ctx.lineWidth = 1;
      roundRect(ctx, px, py, panelW, panelH, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = COLORS.gold;
      ctx.font = '10px "Courier New", monospace';
      ctx.textAlign = 'left';
      ctx.fillText('SPONTANEOUS MARKET INDEX', px + 10, py + 16);

      ctx.fillStyle = COLORS.goldBright;
      ctx.font = 'bold 17px "Courier New", monospace';
      ctx.fillText(this.value.toFixed(1), px + 10, py + 37);

      if (this.history.length > 1){
        var chartX = px + 10, chartY = py + 46, chartW = panelW - 20, chartH = 34;
        ctx.beginPath();
        ctx.strokeStyle = COLORS.amber;
        ctx.lineWidth = 1.4;
        for (var i = 0; i < this.history.length; i++){
          var v = this.history[i];
          var x = chartX + (i / (this.maxPoints - 1)) * chartW;
          var y = chartY + chartH - (v / 100) * chartH;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();
    };

    // ---------------------------------------------------------------------
    // Subtle scrolling background market chart (multi-variable)
    // ---------------------------------------------------------------------
    function BackgroundMarket(w){
      this.step = 4;
      this.series = [
        { color: 'rgba(111,183,255,0.13)', data: [], base: 0.5 },
        { color: 'rgba(127,227,196,0.11)', data: [], base: 0.35 },
        { color: 'rgba(242,197,114,0.10)', data: [], base: 0.65 }
      ];
      this.timer = 0;
      this.init(w);
    }
    BackgroundMarket.prototype.init = function(w){
      var n = Math.ceil(w / this.step) + 2;
      this.series.forEach(function(s){
        s.data = [];
        var v = s.base;
        for (var i = 0; i < n; i++){
          v += rand(-0.01, 0.01);
          v = clamp(v, 0.05, 0.95);
          s.data.push(v);
        }
      });
    };
    BackgroundMarket.prototype.update = function(dt, w, boomBust){
      this.timer += dt;
      var interval = 0.045;
      var maxPoints = Math.ceil(w / this.step) + 2;
      while (this.timer >= interval){
        this.timer -= interval;
        this.series.forEach(function(s){
          var last = s.data[s.data.length - 1];
          var delta = rand(-0.014, 0.014) * boomBust.volMult;
          if (boomBust.phase === 'boom') delta += 0.028;
          if (boomBust.phase === 'bust') delta -= 0.045;
          var v = clamp(last + delta, 0.03, 0.97);
          s.data.push(v);
        });
      }
      this.series.forEach(function(s){
        while (s.data.length > maxPoints) s.data.shift();
      });
    };
    BackgroundMarket.prototype.draw = function(ctx, w, h){
      var baseY = h * 0.12, chartH = h * 0.48;
      this.series.forEach(function(s){
        ctx.beginPath();
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 1.2;
        var n = s.data.length;
        for (var i = 0; i < n; i++){
          var x = w - (n - 1 - i) * 4;
          var y = baseY + chartH - s.data[i] * chartH;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      });
    };

    // ---------------------------------------------------------------------
    // Boom & bust cycle controller - periodically resets network velocity
    // ---------------------------------------------------------------------
    function BoomBust(){
      this.phase = 'calm';
      this.timer = rand(16, 26);
      this.phaseTimer = 0;
      this.volMult = 1;
      this.speedMult = 1;
      this.flash = 0;
    }
    BoomBust.prototype.update = function(dt, onShock){
      if (this.phase === 'calm'){
        this.timer -= dt;
        this.speedMult = lerp(this.speedMult, 1, dt * 2);
        if (this.timer <= 0){
          this.phase = 'boom';
          this.phaseTimer = rand(2.5, 4);
          this.volMult = 2.1;
        }
      } else if (this.phase === 'boom'){
        this.phaseTimer -= dt;
        this.speedMult = lerp(this.speedMult, 1.7, dt * 1.5);
        if (this.phaseTimer <= 0){
          this.phase = 'bust';
          this.phaseTimer = rand(1.2, 2);
          this.volMult = 3;
          this.flash = 1;
          onShock();
        }
      } else if (this.phase === 'bust'){
        this.phaseTimer -= dt;
        this.speedMult = lerp(this.speedMult, 2.6, dt * 3);
        this.flash = Math.max(0, this.flash - dt * 0.8);
        if (this.phaseTimer <= 0){
          this.phase = 'calm';
          this.timer = rand(18, 30);
          this.volMult = 1;
        }
      }
    };
    BoomBust.prototype.drawFlash = function(ctx, w, h){
      if (this.flash <= 0) return;
      ctx.save();
      var g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.75);
      g.addColorStop(0, 'rgba(255,93,93,0)');
      g.addColorStop(1, 'rgba(255,93,93,' + (0.18 * this.flash) + ')');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    };

    // ---------------------------------------------------------------------
    // State variables
    // ---------------------------------------------------------------------
    var agents = [];
    var trades = [];
    var particles = [];
    var quotes = [];
    var marketIndex = new MarketIndex();
    var bgMarket = null;
    var boomBust = new BoomBust();
    var quoteTimer = rand(6, 10);

    function initAgents(w, h){
      var density = (w * h) / 16000;
      var count = clamp(Math.round(density), 26, 70);
      agents = [];
      for (var i = 0; i < count; i++) agents.push(new Agent(w, h));
    }

    function resize(){
      var dpr = window.devicePixelRatio || 1;
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!bgMarket) bgMarket = new BackgroundMarket(W);
      else bgMarket.init(W);
      if (agents.length === 0) initAgents(W, H);
      else agents.forEach(function(a){
        a.x = clamp(a.x, a.r, W - a.r);
        a.y = clamp(a.y, a.r, H - a.r);
      });
    }

    function handleCollisions(){
      for (var i = 0; i < agents.length; i++){
        for (var j = i + 1; j < agents.length; j++){
          var a = agents[i], b = agents[j];
          var dx = b.x - a.x, dy = b.y - a.y;
          var dist = Math.sqrt(dx * dx + dy * dy);
          var minDist = a.r + b.r + 6;
          if (dist < minDist && dist > 0.0001){
            var now = performance.now() / 1000;
            if (now - a.lastTrade > 0.5 && now - b.lastTrade > 0.5){
              a.lastTrade = now;
              b.lastTrade = now;
              trades.push(new TradeLink(a, b));
              particles.push(new PriceParticle((a.x + b.x) / 2, (a.y + b.y) / 2));
              marketIndex.registerTrade();
            }
            var nx = dx / dist, ny = dy / dist;
            var overlap = (minDist - dist) / 2;
            a.x -= nx * overlap;
            a.y -= ny * overlap;
            b.x += nx * overlap;
            b.y += ny * overlap;
            var avx = a.vx, avy = a.vy;
            a.vx = b.vx; a.vy = b.vy;
            b.vx = avx; b.vy = avy;
          }
        }
      }
    }

    function drawBackground(){
      var g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#080b13');
      g.addColorStop(1, '#10182a');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }

    // ---------------------------------------------------------------------
    // Main loop
    // ---------------------------------------------------------------------
    var running = true;
    var lastTime = null;
    var animationFrameId = null;

    function frame(ts){
      if (!running) return;
      if (lastTime === null) lastTime = ts;
      var dt = Math.min((ts - lastTime) / 1000, 0.05);
      lastTime = ts;

      boomBust.update(dt, function(){
        agents.forEach(function(a){ a.scatter(); });
      });
      bgMarket.update(dt, W, boomBust);
      marketIndex.update(dt, boomBust.volMult);

      agents.forEach(function(a){ a.update(dt, W, H, boomBust.speedMult); });
      handleCollisions();

      trades.forEach(function(t){ t.update(dt); });
      trades = trades.filter(function(t){ return t.alive; });

      particles.forEach(function(p){ p.update(dt); });
      particles = particles.filter(function(p){ return p.alive; });

      quoteTimer -= dt;
      if (quoteTimer <= 0){
        quotes.push(new QuoteInstance(W, H));
        quoteTimer = rand(9, 15);
      }
      quotes.forEach(function(q){ q.update(dt); });
      quotes = quotes.filter(function(q){ return q.alive; });

      drawBackground();
      bgMarket.draw(ctx, W, H);
      boomBust.drawFlash(ctx, W, H);

      trades.forEach(function(t){ t.draw(ctx); });
      agents.forEach(function(a){ a.draw(ctx); });
      particles.forEach(function(p){ p.draw(ctx); });
      quotes.forEach(function(q){ q.draw(ctx); });

      marketIndex.draw(ctx, W, H);

      animationFrameId = requestAnimationFrame(frame);
    }

    function tick() {
      running = true;
      lastTime = null;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(frame);
    }

    window.addEventListener('resize', resize);
    resize();
    tick();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        running = false;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      } else {
        tick();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      running = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', resize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="scene"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        display: 'block'
      }}
      role="img"
      aria-label="Animated network of floating nodes forming trade connections, representing spontaneous market order"
    />
  );
};

export default BackgroundCanvas;
